import React, { useCallback, useEffect, useRef, useState } from "react";
import CustomButton from "src/components/CustomButton";
import { GET_SPIN_REWARD, GET_SPIN_REWARD_BY_ID } from "src/graphql/query";
import {
  Box,
  Checkbox,
  debounce,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";
import ActionMenu from "../Business/ActionMenu";
import { getRole, hasPermissionPage } from "src/components/Common/Utils";
import CustomTable from "src/components/CustomTable";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteModel from "src/components/Common/DeleteModel";
import { DELETE_SPIN_REWARD } from "src/graphql/mutations";
import { toast } from "react-toastify";
import Loader from "src/components/Loader";
import ErrorComponent from "src/components/ErrorComponent";
import SpinRewardModal from "./SpinRewardModal";

interface SpinReward {
  _id: string;
}


const SpinReward: React.FC = () => {
  const qrRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openDeleteMultipleModel, setOpenDeleteMultipleModel] =
    useState<boolean>(false);
  const [openDeleteAllModel, setOpenDeleteAllModel] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50 });
  const [total, setTotal] = useState(0);
  const [list, setList] = useState<SpinReward[]>([]);
  const [selectedSpinReward, setSelectedSpinReward] = useState<SpinReward | null>(
    null
  );
  const [isEditSpinReward, setisEditSpinReward] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [DeleteSpinReward] = useMutation(DELETE_SPIN_REWARD);
  const isSuperAdmin = getRole() === "SuperAdmin";
  const initialSpinRewardObj = {
    _id: "",
  };
  const [spinRewardObj, setSpinRewardObj] = useState<any>(initialSpinRewardObj);
  const COLUMNS = [
    {
      headerName: "slug",
      field: "slug",
      flex: 1,
      renderCell: ({ row }: any) => (
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: "pointer" }}
          onClick={() => {
            if (window) {
              window.open(`/SpinReward/${row?._id}`, "_blank");
            }
          }}
        >
          <Typography variant="body2" color="textSecondary">
            {row?.slug}
          </Typography>
        </Box>
      ),
    },

    {
      headerName: "Assumption User",
      field: "assumptionUser",
      flex: 1,
    },
    {
      headerName: "Shift Value",
      field: "shiftValue",
      flex: 1,
    },

    {
      headerName: "Win Quantity",
      field: "winQuantity",
      flex: 1,
    },
    {
      headerName: "Date",
      field: "date",
      flex: 1,
    },
    {
      headerName: "Actions",
      flex: 1,
      renderCell: ({ row }: { row: SpinReward }) => (
        <div>
          {hasPermissionPage("SpinReward", "update") && (
            <Tooltip title="Edit">
              <IconButton
                onClick={() => handleEdit(row)}
                aria-label="edit"
                style={{ marginRight: "8px", color: "#00C5B9" }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          {hasPermissionPage("SpinReward", "delete") && (
            <Tooltip title="Delete">
              <IconButton
                onClick={() => handleDelete(row)}
                aria-label="delete"
                style={{ color: "#00C5B9" }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];


  const { loading, error, data, refetch } = useQuery(GET_SPIN_REWARD, {
    variables: {
      page: pagination.page,
      limit: pagination.pageSize,
    },
  });

  useEffect(() => {
    if (data?.getSpinRewards?.data) {
      const processedData = data?.getSpinRewards?.data.map((spinReward: any) => ({
        ...spinReward,
      }));
      setList(processedData);
      setTotal(data?.getSpinRewards?.count);
    }
  }, [data]);

  const debouncedRefetch = useCallback(
    debounce((term: string) => {
      setSearchTerm(term.length > 0 ? term : "");
      refetch({
        search: term,
        page: pagination.page,
        limit: pagination.pageSize,
      });
    }, 1000),
    [refetch, pagination.page, pagination.pageSize]
  );

  const [getSpinReward] = useLazyQuery(GET_SPIN_REWARD_BY_ID);

  const handleEdit = async (rowData: any) => {
    try {
      if (!rowData?._id) {
        toast.error("Invalid row data");
        console.error("Invalid row data");
        return;
      }

      const res = await getSpinReward({
        variables: {
          _id: rowData._id,
        },
      });

      if (res?.data?.getSpinReward?.statusCode === 200) {
        const removeTypename = (obj: any): any => {
          if (Array.isArray(obj)) {
            return obj.map(removeTypename);
          } else if (typeof obj === "object" && obj !== null) {
            return Object.keys(obj).reduce((acc, key) => {
              if (key !== "__typename") {
                acc[key] = removeTypename(obj[key]);
              }
              return acc;
            }, {} as any);
          }
          return obj;
        };

        const spinRewardData = removeTypename(res?.data?.getSpinReward?.data);
        setSpinRewardObj(spinRewardData);

        setOpenModal(true);
        setisEditSpinReward(true);
      } else {
        toast.error("Failed to fetch Spin Reward details");
        console.error("Failed to fetch Spin Reward details");
      }
    } catch (error: any) {
      toast.error(error?.message);
      console.error(
        "An error occurred while fetching Spin Reward details:",
        error
      );
    }
  };

  const handleDelete = (rowData: SpinReward) => {
    setSelectedSpinReward(rowData);
    setOpenConfirmDialog(true);
  };
  const confirmDelete = async () => {
    try {
      if (selectedSpinReward && selectedSpinReward._id) {
        const res = await DeleteSpinReward({
          variables: {
            _id: selectedSpinReward._id,
          },
        });
        if (res?.data?.deleteSpinReward?.statusCode === 200) {
          toast.success(
            res?.data?.deleteSpinReward?.message ||
            "Spin Reward deleted successfully!"
          );
          refetch();
        } else {
          throw new Error(res?.data?.deleteSpinReward?.message);
        }
      }
    } catch (error: any) {
      toast.error(
        error?.message || "An error occurred while deleting the Spin Reward."
      );
    } finally {
      setOpenConfirmDialog(false);
      setSelectedSpinReward(null);
    }
  };

  const handleCloseModal = () => {
    setSpinRewardObj(initialSpinRewardObj);
    setOpenModal(false);
    setisEditSpinReward(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
    debouncedRefetch(value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch({ search: "", page: 1, limit: pagination.pageSize });
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
    refetch({
      search: searchTerm,
      page: newPage + 1,
      limit: pagination.pageSize,
    });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize }));
    refetch({
      search: searchTerm,
      page: pagination.page,
      limit: newPageSize,
    });
  };

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  return (
    <>
      <Box p={2}>
        {!openModal && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              paddingRight={4}
            >
              <Typography variant="h4" style={{ color: "#00C5B9" }}>
                Spin Reward
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Box width={200}>
                  <TextField
                    label="Search"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton onClick={handleClearSearch}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                {isSuperAdmin && (
                  <ActionMenu
                    isSuperAdmin={isSuperAdmin}
                    setOpenDeleteMultipleModel={setOpenDeleteMultipleModel}
                    setOpenDeleteAllModel={setOpenDeleteAllModel}
                  />
                )}
                {hasPermissionPage("SpinReward", "add") && (
                  <Box>
                    <CustomButton
                      onClick={() => setOpenModal(true)}
                      variant="contained"
                      className="width: 200px"
                    >
                      Create
                    </CustomButton>
                  </Box>
                )}
                <Box></Box>
              </Box>
            </Box>
            <Box mt={2}>
              <CustomTable
                columns={COLUMNS}
                data={list}
                paginationModel={{
                  page: pagination.page - 1,
                  pageSize: pagination.pageSize,
                }}
                totalCount={total}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </Box>
          </>
        )}
        {openModal && (
          <SpinRewardModal
            handleClose={handleCloseModal}
            refetchSpinReward={() => refetch()}
            initialSpinRewardObj={initialSpinRewardObj}
            spinRewardObj={spinRewardObj}
            setSpinRewardObj={setSpinRewardObj}
            isEditSpinReward={isEditSpinReward}
          />
        )}
        <DeleteModel
          open={openConfirmDialog}
          onClose={() => setOpenConfirmDialog(false)}
          onConfirm={confirmDelete}
          title="Confirm Deletion"
          message="Are you sure you want to delete this Spin Reward?"
        />
      </Box>
    </>
  );
}

export default SpinReward;